import TopicAccordion from '../TopicAccordion/TopicAccordion';
import { useCoveredPaths, pathCovered } from '../GrowthPromos/GrowthPromos';
import React, { useState } from 'react';
import classNames from 'classnames';
import { scanProfileText } from '../../util/api';
import { isContactShareBlocked } from '../../util/chatContactFilter';
import {
  childrenOf,
  nodeFor,
  pathLabel,
  pathLabels,
  normalizePath,
  normalizePaths,
  OTHER_MAX,
  PROFILE_PATH_LIMIT,
  SEP,
} from '../../util/topicPaths';
import { PathHeart } from '../TalkAbout/TopicHeart';
import css from './DrillPicker.module.css';

const ICONS = { life: '💬', work: '💼', sports: '🏈', shows: '🎬', world: '🌎', hobbies: '🌱', health: '🩺' };

/** Check custom "Other" text: contact info locally, then the server profile-text filter. */
export const checkOtherText = text => {
  if (isContactShareBlocked(text)) {
    return Promise.resolve('Please leave out contact details.');
  }
  return scanProfileText({ fields: { topicPathOther: text } })
    .then(() => null)
    .catch(e => {
      const status = e?.status || e?.response?.status;
      if (status === 400) return e?.data?.message || "Please reword this. It doesn't meet our Community Guidelines.";
      return null; // filter unavailable: don't block the user
    });
};

/**
 * Multi-select drill-down: category › topic › subtopic. Stop at any level.
 * value: array of paths. Selected paths show as removable tags.
 */
const SuggestTopic = ({ source }) => {
  const [open, setOpen] = useState(false);
  const [topic, setTopic] = useState('');
  const [category, setCategory] = useState('');
  const [state, setState] = useState(null);
  const [msg, setMsg] = useState(null);
  const send = () => {
    const t = topic.trim();
    if (t.length < 2) return setMsg('Please type a topic.');
    if (isContactShareBlocked(t) || isContactShareBlocked(category)) {
      return setMsg('Please leave out phone numbers, emails, links or handles.');
    }
    setState('sending');
    setMsg(null);
    fetch('/api/topic-suggestion', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ topic: t, category, source }),
    })
      .then(r => r.json().then(b => ({ ok: r.ok, b })))
      .then(({ ok, b }) => {
        if (ok && b.ok) setState('done');
        else {
          setState(null);
          setMsg(b.message || 'Something went wrong. Please try again.');
        }
      })
      .catch(() => {
        setState(null);
        setMsg('Something went wrong. Please try again.');
      });
  };
  if (state === 'done') {
    return <p className={css.suggestThanks} role="status" data-suggest-thanks="true">Thanks! We’ll review your topic idea.</p>;
  }
  if (!open) {
    return (
      <button type="button" className={css.suggestLink} onClick={() => setOpen(true)} data-suggest-topic="true">
        Don’t see your topic? Suggest a topic
      </button>
    );
  }
  return (
    <div className={css.suggestBox} data-suggest-form="true">
      <label className={css.suggestLabel}>
        Topic idea
        <input value={topic} maxLength={60} onChange={e => setTopic(e.target.value)} placeholder="e.g. Birdwatching" />
      </label>
      <label className={css.suggestLabel}>
        Category (optional)
        <select value={category} onChange={e => setCategory(e.target.value)}>
          <option value="">Not sure</option>
          {childrenOf('').map(c => (
            <option key={c.path} value={c.label}>{c.label}</option>
          ))}
        </select>
      </label>
      {msg ? <p className={css.error} role="alert">{msg}</p> : null}
      <button type="button" onClick={send} disabled={state === 'sending'}>
        {state === 'sending' ? 'Sending…' : 'Send suggestion'}
      </button>
    </div>
  );
};

const topicNoteFor = path => {
  const parts = String(path).split('/');
  for (let i = parts.length; i > 0; i -= 1) {
    const n = nodeFor(parts.slice(0, i).join('/'));
    if (n && n.note) return n.note;
  }
  return null;
};

const LAST_AT = {};

const DrillPicker = props => {
  if (props.accordion) {
    // Same inline +/- accordion as search (Sam, Oct 21 2026).
    const sel = normalizePaths(props.value || [], props.limit || PROFILE_PATH_LIMIT);
    return (
      <div className={props.className} data-drill-accordion="true">
        {props.title ? <h3 className={css.title || undefined}>{props.title}</h3> : null}
        {props.hint ? <p className={css.hint || undefined}>{props.hint}</p> : null}
        <TopicAccordion value={sel} onChange={props.onChange} limit={props.limit || PROFILE_PATH_LIMIT} idPrefix={props.id || 'drill'} />
        <p data-topic-picked={sel.length} style={{ display: 'flex', gap: 12, alignItems: 'center', fontWeight: 600 }}>
          <span>{sel.length === 1 ? '1 topic picked' : `${sel.length} topics picked`}</span>
          {sel.length ? (
            <button type="button" onClick={() => props.onChange && props.onChange([])} style={{ background: 'none', border: '1.5px solid #ccc', borderRadius: 999, padding: '6px 14px', font: 'inherit', cursor: 'pointer' }} data-topic-clear="true">Clear</button>
          ) : null}
        </p>
      </div>
    );
  }
  return <DrillPickerInner {...props} />;
};
const DrillPickerInner = props => {
  const {
    value = [],
    onChange,
    limit = PROFILE_PATH_LIMIT,
    title,
    hint,
    submitLabel,
    onSubmit,
    className,
    id = 'drill',
    showFeaturedOnly = false,
    onBrowse,
    hearts = false,
    afterSelected = null,
    afterPicker = null,
    collapseWhenSelected = false,
    onlyCovered = false,
    firstListenerNotes = false,
    talkerEmptyNotes = false,
  } = props;
  const covered = useCoveredPaths();
  const selected = normalizePaths(value, limit);
  // Remember the drill level across remounts (e.g. /s re-renders after each pick), so the picker stays put.
  const [at, setAt] = useState(() => (normalizePaths(value, limit).length ? LAST_AT[id] || '' : ''));
  React.useEffect(() => {
    LAST_AT[id] = at;
  }, [at, id]);
  const [other, setOther] = useState('');
  const [otherError, setOtherError] = useState(null);
  const [checking, setChecking] = useState(false);
  const [expanded, setExpanded] = useState(false);
  React.useEffect(() => {
    if (onBrowse) onBrowse(at);
  }, [at]); // eslint-disable-line react-hooks/exhaustive-deps
  const full = selected.length >= limit;
  const nodes = childrenOf(at).filter(
    n => n && (!showFeaturedOnly || !n.unfeatured) && (!onlyCovered || pathCovered(covered, n.path))
  );
  const crumbs = at ? pathLabels(at) : [];
  const crumbPaths = at ? at.split('/').map((_, i, a) => a.slice(0, i + 1).join('/')) : [];

  const add = path => {
    const p = normalizePath(path);
    if (!p || full) return;
    const next = normalizePaths([...selected.filter(s => s !== p), p], limit);
    onChange && onChange(next);
    // Stay at the current level so several subtopics can be picked in a row (Sam, Oct 15).
    setExpanded(true);
    setOther('');
    setOtherError(null);
  };
  const remove = path => onChange && onChange(selected.filter(s => s !== path));

  const addOther = () => {
    const text = other.replace(/\s+/g, ' ').trim().slice(0, OTHER_MAX);
    if (!text) return;
    setChecking(true);
    checkOtherText(text).then(err => {
      setChecking(false);
      if (err) setOtherError(err);
      else add(at ? `${at}/~${text}` : `~${text}`);
    });
  };

  return (
    <div className={classNames(css.root, className)} data-drill-picker={id}>
      {title ? <p className={css.title}>{title}</p> : null}
      {hint ? <p className={css.hint}>{hint}</p> : null}

      {selected.length ? (
        <ul className={css.tags} data-drill-selected={selected.length}>
          {selected.map(p => (
            <li key={p}>
              <button type="button" className={css.tag} onClick={() => remove(p)} aria-label={`Remove ${pathLabel(p)}`}>
                {pathLabel(p)} <span aria-hidden="true">×</span>
              </button>
              {hearts ? <PathHeart path={p} /> : null}
            </li>
          ))}
        </ul>
      ) : null}

      {afterSelected}

      {collapseWhenSelected && selected.length && !expanded && !full ? (
        <button type="button" className={css.expandBtn} onClick={() => setExpanded(true)} data-drill-expand="true">
          + Add another topic
        </button>
      ) : full ? (
        <p className={css.limit}>{limit === 1 ? 'Tap × to change it.' : `You’ve picked ${limit}. Remove one to add another.`}</p>
      ) : (
        <>
          {at && nodeFor(String(at).split('/')[0])?.tagline ? (
          <p className={css.topicNote} data-topic-tagline="true">{nodeFor(String(at).split('/')[0]).tagline}</p>
        ) : null}
          {at && topicNoteFor(at) ? (
          <p className={css.topicNote} data-topic-note-disclaimer="true">{topicNoteFor(at)}</p>
        ) : null}
        {at ? (
            <button type="button" className={css.backBtn} onClick={() => setAt(crumbPaths.length > 1 ? crumbPaths[crumbPaths.length - 2] : '')} data-drill-back="top">
              ← Back to {crumbPaths.length > 1 ? crumbs[crumbs.length - 2] : 'topics'}
            </button>
          ) : null}
        <nav className={css.crumbs} aria-label="Topic level" data-drill-breadcrumb={at || 'root'}>
            <button type="button" className={classNames(css.crumb, { [css.crumbOn]: !at })} onClick={() => setAt('')}>
              {selected.length && limit > 1 ? 'Add another' : 'All topics'}
            </button>
            {crumbs.map((label, i) => (
              <React.Fragment key={crumbPaths[i]}>
                <span className={css.sep} aria-hidden="true">›</span>
                <button
                  type="button"
                  className={classNames(css.crumb, { [css.crumbOn]: i === crumbs.length - 1 })}
                  onClick={() => setAt(crumbPaths[i])}
                >
                  {label}
                </button>
              </React.Fragment>
            ))}
          </nav>

          {at ? (
            <div className={css.stopRow}>
              <button type="button" className={css.stopHere} onClick={() => add(at)} data-drill-stop="true">
                ✓ Add “{crumbs.join(SEP)}”
              </button>
              {hearts ? <PathHeart path={at} className={css.levelHeart} /> : null}
            </div>
          ) : null}

          <ul className={classNames(css.grid, { [css.gridTop]: !at })} data-drill-level={crumbs.length + 1}>
            {nodes.map(n => {
              const hasKids = !!(n.children && n.children.length);
              const isOn = selected.includes(n.path);
              return (
                <li key={n.path} className={hearts ? css.tileWithHeart : undefined}>
                  <button
                    type="button"
                    className={classNames(css.step, { [css.stepOn]: isOn })}
                    onClick={() => (hasKids ? setAt(n.path) : add(n.path))}
                    data-drill-node={n.path}
                  >
                    {!at && ICONS[n.id] ? <span className={css.icon} aria-hidden="true">{ICONS[n.id]}</span> : null}
                    <span className={css.stepLabel}>
                      {n.label}
                      {!at && n.path === 'just-talk' ? (
                        <span style={{ display: 'block', fontSize: 12, fontWeight: 400, opacity: 0.75 }} data-just-talk-sub="true">
                          Not sure what to talk about? Start here.
                        </span>
                      ) : null}
                      {/* Empty-topic notes sit under the title (inside the label column) so they
                          stack cleanly instead of becoming a third flex column that overlaps it. */}
                      {talkerEmptyNotes && covered && !pathCovered(covered, n.path) ? (
                        <small className={css.emptyNote} data-talker-empty-note={n.path}>Be the first to find a listener</small>
                      ) : null}
                      {firstListenerNotes && covered && !pathCovered(covered, n.path) ? (
                        <small className={css.emptyNote} data-first-listener-note={n.path}>No listeners yet. Be the first!</small>
                      ) : null}
                    </span>
                    <span className={css.chev} aria-hidden="true">{hasKids ? '›' : isOn ? '✓' : '+'}</span>
                  </button>
                  {hearts ? <PathHeart path={n.path} className={css.tileHeart} /> : null}
                </li>
              );
            })}
          </ul>

          {at ? (
            <button type="button" className={classNames(css.backBtn, css.backBtnBottom)} onClick={() => { setAt(crumbPaths.length > 1 ? crumbPaths[crumbPaths.length - 2] : ''); if (typeof window !== 'undefined') { const el = document.querySelector('[data-drill-picker="' + id + '"]'); if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' }); } }} data-drill-back="bottom">
              {selected.length ? `Done, back to ${crumbPaths.length > 1 ? crumbs[crumbs.length - 2] : 'topics'} (${selected.length} picked)` : `← Back to ${crumbPaths.length > 1 ? crumbs[crumbs.length - 2] : 'topics'}`}
            </button>
          ) : null}

          <div className={css.other}>
            <label htmlFor={`${id}-other`} className={css.otherLabel}>
              Other{at ? ` in ${nodeFor(at)?.label || ''}` : ''}: type it
            </label>
            <div className={css.otherRow}>
              <input
                id={`${id}-other`}
                type="text"
                value={other}
                maxLength={OTHER_MAX}
                placeholder={at ? 'Something more specific' : 'Something else'}
                onChange={e => {
                  setOther(e.target.value);
                  setOtherError(null);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addOther();
                  }
                }}
              />
              <button type="button" onClick={addOther} disabled={!other.trim() || checking}>
                {checking ? '…' : 'Add'}
              </button>
            </div>
            {otherError ? <p className={css.error} role="alert">{otherError}</p> : null}
          </div>
        </>
      )}

      {props.suggest === false ? null : <SuggestTopic source={id} />}

      {afterPicker}

      {onSubmit ? (
        <button
          type="button"
          className={css.submit}
          onClick={() => onSubmit(selected.length ? selected : at ? [at] : [])}
          data-drill-submit="true"
        >
          {submitLabel || 'See listeners →'}
        </button>
      ) : null}
    </div>
  );
};

export default DrillPicker;
